import React, { useState, useRef, useEffect } from 'react';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';

import { FormattedMessage } from '../../util/reactIntl';
import {
  START_DATE,
  getStartOf,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
} from '../../util/dates';
import FieldDateRangeInput from '../FieldDateRangeInput/FieldDateRangeInput';
import {
  buildIsDayBlocked,
  buildIsOutsideRange,
  buildIsBlockedBetween,
  isSelectionValid,
  hasAnySelectableRange,
} from './cartDateEditorHelpers';
import css from './CartDrawerDateRangePicker.module.css';

// ZONE BRIDGE - same as BookingDatesForm.js's own format/parse on this field. The form
// value (and the cart item it came from / is written to) is a pair of instants:
// midnight of the chosen days in the BOOKING's zone. The calendar underneath works
// in the browser's zone. `format` shows an instant as the same wall-clock day
// locally; `parse` turns the picked local day back into the booking-zone midnight.
// This used to be `identity` on both sides, so for any browser not in the booking's
// zone the calendar highlighted the day before/after the one the fields above it
// named, and Apply wrote back local midnights labelled with the booking's zone.
const formatForPicker = timeZone => v => {
  const { startDate, endDate } = v || {};
  return v
    ? {
        startDate: startDate && timeZone ? timeOfDayFromTimeZoneToLocal(startDate, timeZone) : startDate,
        endDate: endDate && timeZone ? timeOfDayFromTimeZoneToLocal(endDate, timeZone) : endDate,
      }
    : v;
};
const parseFromPicker = timeZone => v => {
  const { startDate, endDate } = v || {};
  const toZoneMidnight = d =>
    d && timeZone ? getStartOf(timeOfDayFromLocalToTimeZone(d, timeZone), 'day', timeZone) : d;
  return v ? { startDate: toZoneMidnight(startDate), endDate: toZoneMidnight(endDate) } : v;
};
// The calendar's day cells (and the moments DateRangeInput hands isBlockedBetween) are
// browser-local wall-clock days; cartDateEditorHelpers' predicates take INSTANTS and
// read them in the booking's zone (that's what their tests pin down). Bridge each cell
// the same way `parse` above does before it reaches them - otherwise a browser east or
// west of the booking's zone asks about the wrong calendar day.
const cellToInstant = timeZone => day => {
  if (!day) return day;
  const asDate = typeof day.toDate === 'function' ? day.toDate() : new Date(day);
  return timeZone ? timeOfDayFromLocalToTimeZone(asDate, timeZone) : asDate;
};
const nextMonthFn = (currentMoment, timeZone) =>
  getStartOf(currentMoment, 'month', timeZone, 1, 'months');
const prevMonthFn = (currentMoment, timeZone) =>
  getStartOf(currentMoment, 'month', timeZone, -1, 'months');

/**
 * RAS-2511: inline day-range picker for a cart-item group. Reuses FieldDateRangeInput
 * (the same field BookingDatesForm.js uses on the listing page's booking panel)
 * rather than a parallel calendar implementation, including its own built-in
 * Cancel/Apply footer - that footer is the only control surface here, matching the
 * listing page exactly (no second, competing Apply/Clear row layered on top).
 *
 * Two behaviors are ported directly from BookingDatesForm.js rather than re-derived,
 * since they're load-bearing for react-dates working correctly, not stylistic
 * choices:
 * - `onFocus`: tapping the Start date side clears BOTH dates first
 *   (form.change('dateRange', null)), exactly like BookingDatesForm.js's own
 *   `if (input === START_DATE) { formApi.change('bookingDates', null); }`. Without
 *   this, re-picking a range after one is already set is unreliable - react-dates
 *   needs to see a clean start from null, not a click layered on top of a still-set
 *   complete range.
 * - `focusedInput`/`onFocusedInputChange`: DateRangeInput.js's focusedInput is a
 *   controlled prop with no internal default - BookingDatesForm.js always owns this
 *   state too, seeded to START_DATE, or the calendar has no active input to
 *   attribute clicks to at all.
 */
const CartDrawerDateRangePicker = ({
  initialStartDate,
  initialEndDate,
  blockedDates,
  checkedDates,
  isLoadingAvailability,
  minimumDays,
  timeZone,
  intl,
  onApply,
  onClear,
  onMonthNavigate,
  onCurrentMonthChange,
}) => {
  const [applyInProgress, setApplyInProgress] = useState(false);
  const [applyError, setApplyError] = useState(null);
  const [focusedInput, setFocusedInput] = useState(START_DATE);
  // RAS-2506 follow-up: the picker's own availability fetch is now lazy (current +
  // next month up front, then one more month at a time as the user navigates) rather
  // than eagerly fetching the whole year-long booking window on open - see
  // useCartGroupAvailability.js and CartDatesEditor's handleToggle. currentMonth
  // tracks the calendar's visible month purely to compute which month to fetch next;
  // it doesn't drive rendering directly (react-dates owns its own visible month).
  // Reported up to CartDatesEditor via onCurrentMonthChange (Gap C follow-up) so a
  // listing added to the group while the picker is already open can be caught up
  // through whatever month is actually on screen, not just the picker's default
  // initial range.
  const [currentMonth, setCurrentMonth] = useState(() =>
    getStartOf(initialStartDate || new Date(), 'month', timeZone)
  );
  useEffect(() => {
    onCurrentMonthChange?.(currentMonth);
    // Only re-report when currentMonth itself changes - onCurrentMonthChange isn't
    // expected to be a stable reference across renders (CartDatesEditor passes an
    // inline closure), and including it would re-fire this on every unrelated render.
    // eslint-disable-next-line
  }, [currentMonth]);
  // Belt-and-suspenders alongside the built-in Apply button's own applyInProgress
  // disabled state below - guards the logic path directly against a double-submit
  // that slips through before that prop's re-render takes effect.
  const applyInProgressRef = useRef(false);
  const isMountedRef = useRef(true);
  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const noAvailableDates = !isLoadingAvailability && !hasAnySelectableRange(blockedDates, timeZone, minimumDays);

  if (noAvailableDates) {
    return (
      <div className={css.root}>
        <p className={css.noAvailability}>
          <FormattedMessage
            id="CartDrawerDateRangePicker.noAvailableDates"
            defaultMessage="No available dates for this period."
          />
        </p>
      </div>
    );
  }

  return (
    <FinalForm
      onSubmit={() => {}}
      initialValues={{ dateRange: { startDate: initialStartDate, endDate: initialEndDate } }}
      // CartDatesEditor re-renders on every progressive RAS-2509 availability
      // update while this picker is open, recomputing initialStartDate/
      // initialEndDate as brand-new Date objects each time (same instant, new
      // reference). Without this, react-final-form treats every one of those as a
      // genuine initialValues change and resets the form mid-edit - the in-progress
      // selection would revert on its own a moment after every click. Mirrors
      // CartDrawer.js's own main FinalForm, which guards against the identical
      // class of bug for the same reason.
      initialValuesEqual={(a, b) => isEqual(a, b)}
      render={({ values, form }) => {
        const startDate = values.dateRange?.startDate;
        const endDate = values.dateRange?.endDate;
        const toInstant = cellToInstant(timeZone);

        const handleRealApply = async () => {
          if (applyInProgressRef.current) return;
          // Defensive re-check - isDayBlocked/isOutsideRange/isBlockedBetween already
          // keep the calendar from producing an invalid selection, this just guards
          // against blockedDates having changed since the last render.
          if (!isSelectionValid(startDate, endDate, blockedDates, checkedDates, timeZone, minimumDays)) return;

          applyInProgressRef.current = true;
          setApplyInProgress(true);
          setApplyError(null);
          try {
            await onApply({ startDate, endDate });
            // On success the parent (CartDatesEditor) unmounts this component -
            // don't touch state after that.
          } catch (e) {
            applyInProgressRef.current = false;
            if (isMountedRef.current) {
              setApplyError(e?.message || 'Something went wrong applying the new dates. Please try again.');
              setApplyInProgress(false);
            }
          }
        };

        return (
          <div className={css.root}>
            <FieldDateRangeInput
              className={css.field}
              name="dateRange"
              isDaily
              inline
              startDateId="CartDrawerDateRangePicker.startDate"
              endDateId="CartDrawerDateRangePicker.endDate"
              format={formatForPicker(timeZone)}
              parse={parseFromPicker(timeZone)}
              validate={() => undefined}
              onBlur={() => {}}
              onFocus={input => {
                if (input === START_DATE) {
                  form.change('dateRange', { startDate: null, endDate: null });
                }
              }}
              focusedInput={focusedInput}
              onFocusedInputChange={setFocusedInput}
              clearButtonLabel={intl.formatMessage({
                id: 'CartDrawerDateRangePicker.clear',
                defaultMessage: 'Clear',
              })}
              onApply={handleRealApply}
              applyInProgress={applyInProgress}
              onRemove={() => {
                setFocusedInput(START_DATE);
                setApplyError(null);
                onClear?.();
              }}
              isDayBlocked={() => {
                const predicate = buildIsDayBlocked(blockedDates, checkedDates, timeZone, minimumDays, startDate);
                return day => predicate(toInstant(day));
              }}
              isOutsideRange={() => {
                const predicate = buildIsOutsideRange(timeZone);
                return day => predicate(toInstant(day));
              }}
              isBlockedBetween={(from, to) =>
                buildIsBlockedBetween(blockedDates, checkedDates, timeZone)(toInstant(from), toInstant(to))
              }
              onPrevMonthClick={() => {
                setCurrentMonth(prevMonth => {
                  const newMonth = prevMonthFn(prevMonth, timeZone);
                  // Keep a 1-month buffer ahead of whatever's now visible, matching
                  // FieldDateAndTimeInput's own onMonthClick pattern - react-dates
                  // pre-renders the next invisible month, so it needs data too by the
                  // time it's reachable.
                  onMonthNavigate?.(prevMonthFn(newMonth, timeZone));
                  return newMonth;
                });
              }}
              onNextMonthClick={() => {
                setCurrentMonth(prevMonth => {
                  const newMonth = nextMonthFn(prevMonth, timeZone);
                  onMonthNavigate?.(nextMonthFn(newMonth, timeZone));
                  return newMonth;
                });
              }}
            />

            {isLoadingAvailability && (
              <p className={css.loadingHint}>
                <FormattedMessage
                  id="CartDrawerDateRangePicker.loadingAvailability"
                  defaultMessage="Checking availability…"
                />
              </p>
            )}
            {applyError && <p className={css.error}>{applyError}</p>}
          </div>
        );
      }}
    />
  );
};

export default CartDrawerDateRangePicker;
